import React, { useState } from 'react';

import { Typography, Grid, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import playButton from '../assets/playAudio3.svg';
import pauseButton from '../assets/pauseAudio.svg';
import erikaAudio from '../assets/audio/theFactorsErika.wav';
import karenAudio from '../assets/audio/theFactorsKaren.wav';
import pattiAudio from '../assets/audio/theFactorsPatti.wav';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    marginTop: '6.25rem',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      marginTop: '3.125rem'
    }
  },
  textBlock1: {
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  textWAudio: {
    width: '51%',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  textBlock2: {
    width: '81%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  audioCaptions: {
    width: '100%',
    marginLeft: '3rem',
    marginBottom: '2.125rem'
  },
  audioCaptionCont: {
    width: '13%',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  boldYellow: {
    color: theme.palette.common.yellow,
    fontFamily: 'proximaNovaB'
  },
  boldFont: {
    fontFamily: 'proximaNovaB'
  },
  button: {
    width: '88%',
    padding: 0,
    marginTop: '.75rem',
    '&:hover': {
      transform: 'scale(1.1,1.1)'
    }
  },
  button1: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  },
  //   ------------------------------------------------------------------------------------ mobile
  mobileWrapper: {
    flexWrap: 'nowrap',
    overflowX: 'scroll'
  },
  mobileAudioCapCont: {
    marginTop: '3.125rem',
    minWidth: '278px',
    textAlign: 'center',
    padding: '2rem 0',
    borderTop: '1px solid black',
    borderBottom: '1px solid black'
  },
  buttonMobile: {
    width: '40%'
  },
  mobileCaptionText: {
    height: '150px'
  }
}));

const nameToAudio = {
  karen: new Audio(karenAudio),
  erika: new Audio(erikaAudio),
  patti: new Audio(pattiAudio)
};

const TheFactors = () => {
  const classes = useStyles();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [personPlaying, setPersonPlaying] = useState('');

  const audioClickHandler = (name) => {
    if (isPlaying) {
      nameToAudio[personPlaying].pause();
      setIsPlaying(false);
      if (personPlaying !== name) {
        nameToAudio[name].play();
        setIsPlaying(true);
      }
    } else {
      nameToAudio[name].play();
      setIsPlaying(true);
    }
  };

  const mobileAudioCaption = (
    <Grid container className={classes.mobileWrapper}>
      {/* ------------------------------------------------------------------------------ patti  */}
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "If I would have gone to my doctors, I wouldn't have been a cervical cancer survivor."{' '}
          <br />
          <span className={classes.boldFont}>Patti Murillo-Casa</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('patti');
            setPersonPlaying('patti');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      {/* ------------------------------------------------------------------------------ karen  */}
      <Grid
        container
        justifyContent="center"
        className={classes.mobileAudioCapCont}
        style={{ marginLeft: '1rem', marginRight: '1rem' }}
      >
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "Having insurance is essential for your survival..."
          <br />
          <span className={classes.boldFont}>Karen Eubanks Jackson</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('karen');
            setPersonPlaying('karen');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'karen' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      {/* ------------------------------------------------------------------------------ erika  */}
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          “At the initial level– the types of providers that black women and white women are seeing–
          we're seeing inequities, and then we see the domino effects..."
          <br />
          <span className={classes.boldFont}>Erika Stallings</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('erika');
            setPersonPlaying('erika');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'erika' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Grid container alignItems="center" direction="column" className={classes.wrapper}>
      <Grid container className={classes.textBlock1}>
        <Typography variant="h2" style={{ marginBottom: '3.125rem' }}>
          The Factors that Lead to Disparity
        </Typography>
        <Typography variant="body1" style={{ marginBottom: '3.125rem' }}>
          Diagnoses of breast cancer in Black women have increased by 0.4% a year since 1975.
          <sup style={{ fontFamily: 'proximaNovaReg', fontSize: '.8rem' }}> 11</sup> This doesn’t
          necessarily mean that Black women are getting cancer more often than they were prior to
          the 70s; rather, increased rates could be attributed to more access to screenings and
          testing. This could potentially be a good thing: more diagnosis should lead to more care.
        </Typography>
        <Typography variant="body1" style={{ marginBottom: '3.125rem' }}>
          However, one recent study commissioned by Hologic found that even the kinds of mammograms
          that Black women were receiving were inferior. This study showed that although screening
          via 3D Mammography improved both recall rates and cancer detection for women of all races,
          Black women were less likely to receive a 3D Mammogram and less likely to be screened
          multiple times during the 5-year period compared to white women. So, while rates of
          detection are up, they aren’t up enough, nor are the rates of follow-up screenings which
          have proven to be essential in keeping cancer in check.
          <sup style={{ fontFamily: 'proximaNovaReg', fontSize: '.8rem' }}> 12</sup>
        </Typography>
      </Grid>
      <Grid container className={classes.textWAudio}>
        <Grid container className={classes.textBlock2}>
          <Typography variant="body1" style={{ marginBottom: '3.125rem' }}>
            In some cases, new guidelines recommending{' '}
            <span className={classes.boldYellow}>reduced screening frequency</span> may also be
            contributing to disparities, thus limiting opportunities for Black and Hispanic women to
            discover if they are at risk. This puts a dent in the progress made against cervical
            cancer rates while impairing women’s access to important healthcare. Routine screening
            with a Pap test alone is recommended for women ages 21-29. For women ages 30-65, certain
            studies show that screening with a Pap test in combination with an HPV test (also known
            as “Pap+HPV” Together) is the best way to detect disease. Women should discuss with
            their healthcare provider about what may be best for them.
          </Typography>
          <Typography variant="body1">
            In addition to a lack of access to thorough screening, Black and Hispanic women are much
            more likely to be <span className={classes.boldYellow}>uninsured</span> or to depend on
            public insurance compared to white women.
            <sup style={{ fontFamily: 'proximaNovaReg', fontSize: '.8rem' }}> 13</sup> This may
            explain why women of color aren’t getting the same types of mammograms, and also
            suggests that their <span className={classes.boldYellow}>access to treatment </span>
            is significantly limited compared to other groups even when they know they have cancer.
          </Typography>
        </Grid>
        <Grid container direction="column" className={classes.audioCaptionCont}>
          {/* ---------------------------------------------------------------------------------------- patti  */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              "If I would have gone to my doctors, I wouldn't have been a cervical cancer survivor."
              <br />
              <span className={classes.boldFont}>Patti Murillo-Casa</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('patti');
                setPersonPlaying('patti');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
          {/* ---------------------------------------------------------------------------------------- karen */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              "Having insurance is essential for your survival..."
              <br />
              <span className={classes.boldFont}>Karen Eubanks Jackson</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('karen');
                setPersonPlaying('karen');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'karen' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
          {/* ---------------------------------------------------------------------------------------- Erika  */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              “At the initial level– the types of providers that black women and white women are
              seeing– we're seeing inequities, and then we see the domino effects..."
              <br />
              <span className={classes.boldFont}>Erika Stallings</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('erika');
                setPersonPlaying('erika');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'erika' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {matchesSM ? mobileAudioCaption : null}
    </Grid>
  );
};

export default TheFactors;
